import React from 'react';
import { useDropzone } from 'react-dropzone';
import uploadIcon from '../../../assets/icons/upload.svg';
import pdfIcon from '../../../assets/images/pdfIcons.svg';
import docIcon from '../../../assets/images/docIcon.svg';
import selected from '../../../assets/images/select-box-circle-fill.svg';
import deleteIcon from '../../../assets/icons/delete.svg';

const DropZoneSignature = ({
  name,
  onChange,
  value = [],
  condition = true,
  height,
}) => {
  const [files, setFiles] = React.useState(value);
  const [error, setError] = React.useState('');

  // Sync local state with Formik's `value` whenever it changes
  React.useEffect(() => {
    setFiles(value || []); // Ensure value is always an array
  }, [value]);

  // Handle file drop
  const onDrop = React.useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (files.length >= 1) {
        setError('You can only upload a maximum of 1 files.');
        return;
      }

      const sizeError = acceptedFiles.filter(
        (file) => file.size > 10 * 1024 * 1024 // 50MB limit
      );

      if (sizeError.length > 0) {
        setError('File size should not exceed 10MB');

        return;
      } else {
        setError(''); // Clear error if no issue with file size
      }
      setFiles((prevFiles) => {
        const newFiles = acceptedFiles?.filter(
          (file) =>
            !prevFiles?.some(
              (prevFile) =>
                prevFile?.name === file?.name || prevFile?.path === file?.path
            )
        );
        const updatedFiles = [...prevFiles, ...newFiles];
        if (onChange) onChange(updatedFiles); // Notify parent (Formik)
        return updatedFiles;
      });
    },
    [onChange]
  );

  // Handle file deletion
  const handleDelete = (fileName) => {
    setFiles((prevFiles) => {
      let updatedFiles = '';
      if (typeof prevFiles === 'string') {
        updatedFiles = prevFiles?.filter((file) => file !== fileName);
        // Handle the case where prevFiles is a string
      } else {
        updatedFiles = prevFiles?.filter((file) => file.path !== fileName);
        // Handle the case where prevFiles is not a string
      }
      if (onChange) onChange(updatedFiles); // Notify parent (Formik)
      return updatedFiles;
    });
  };

  // Initialize Dropzone
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
    },
    onDrop,
  });

  // Sync `files` with the parent component or Formik
  React.useEffect(() => {
    if (onChange) {
      onChange(files); // Sync updated files with the parent (Formik)
    }
  }, [files]);

  return (
    <div>
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <div className="space-y-4">
          <div className="border-dashed border-2 border-gray-300 rounded-md p-6 text-center">
            <p className="text-gray-500">
              Choose a file or drag & drop it here.
            </p>
            <p className="text-sm text-gray-400">
              (PDF, DOC, DOCX formats, up to 10 MB)
            </p>
            <button className="mt-4 px-4 py-2 bg-[#176448] text-white rounded-md">
              Browse File
            </button>
          </div>
        </div>
      </div>

      <div className="text-red-500 text-[12px]">{error}</div>

      {files.length !== 0 && (
        <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[15rem] pt-3">
          {files?.map((file) => {
            const filePath = file?.path || file;
            const fileExtension = filePath
              ?.split?.('.')
              ?.pop?.()
              ?.toLowerCase?.(); // Get the file extension

            let fileIcon = null;

            // Check file type and assign the appropriate icon
            if (fileExtension === 'pdf') {
              fileIcon = pdfIcon; // Set PDF icon
            } else if (fileExtension === 'doc' || fileExtension === 'docx') {
              fileIcon = docIcon; // Set DOC icon
            }

            return (
              <div
                key={file?.path}
                className="flex items-center space-x-2 border border-[#E2E4E9] w-[222px] h-[72px] rounded-[12px] p-3"
              >
                {fileIcon && (
                  <img
                    src={fileIcon}
                    alt="file icon"
                    className="w-[40px] h-[40px] flex-shrink-0"
                  />
                )}

                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center w-full">
                    <div className="font-medium leading-[20px] text-[14px] text-[#0A0D14] truncate max-w-[150px]">
                      {filePath}
                    </div>

                    <img
                      src={deleteIcon}
                      alt="delete icon"
                      className="w-[20px] h-[20px] cursor-pointer flex-shrink-0"
                      onClick={() => handleDelete(filePath)}
                    />
                  </div>

                  <div className="flex justify-between items-center text-[12px] text-[#868C98] pt-1">
                    {file?.size ? (
                      <span className="truncate">
                        {(file?.size / 1024)?.toFixed(2)} KB
                      </span>
                    ) : (
                      <span></span>
                    )}
                    <span className="flex items-center">
                      <img
                        src={selected}
                        alt="logo"
                        className="w-[16px] h-[16px]"
                      />
                      <span className="ml-1 text-[#0A0D14]">Completed</span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DropZoneSignature;
